/**
 * Cents → "$1,234.56". `null`/`undefined` → "—".
 * Negative values render with a leading minus sign before the currency.
 */
export function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const formatted = (abs / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${negative ? '-' : ''}$${formatted}`;
}

/**
 * Cents + currency code → "$1,234.56 USD". Currency tail is dropped for USD,
 * which is the only currency we use today; we keep the suffix for anything
 * else so a future EUR/GBP doesn't silently render as dollars.
 */
export function formatMoney(
  cents: number | null | undefined,
  currency: string | null | undefined = 'USD',
): string {
  const base = dollars(cents);
  if (base === '—' || !currency || currency.toUpperCase() === 'USD') return base;
  return `${base} ${currency.toUpperCase()}`;
}

/**
 * Snake/lower/Title-case status string → display form ("deferred" → "Deferred",
 * "hardware_auth" → "Hardware auth"). Falsy → "—".
 */
export function formatStatus(status: string | null | undefined): string {
  if (!status) return '—';
  const spaced = status.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * Best-effort US phone formatter. 10 digits → "(425) 555-0188",
 * 11 digits starting with 1 → "+1 (425) 555-0188". Anything else passes
 * through so we don't mangle international or partial numbers.
 */
export function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '—';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return raw;
}

/**
 * ISO date or datetime → "Jun 14".
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * ISO date or datetime → "Jun 14, 2026".
 */
export function formatDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * ISO date (date-only or datetime) → "July 9, 2026" with the full month name.
 * Date-only strings are pinned to local midnight so they never slip a day in
 * timezones behind UTC.
 */
export function formatDateFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
  const d = isDateOnly ? new Date(`${iso}T00:00:00`) : new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Byte count → "40 KB" / "1.2 MB".
 */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * ISO datetime → "9:05 AM".
 */
export function formatTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * ISO datetime → "Jun 14, 9:05 AM".
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return `${formatDate(iso)}, ${formatTime(iso)}`;
}

/**
 * Seconds → "2:05:30" for ≥1h, "5:30" for <1h.
 */
export function formatDuration(seconds: number): string {
  const sign = seconds < 0 ? '-' : '';
  const s = Math.abs(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (h > 0) return `${sign}${h}:${pad(m)}:${pad(sec)}`;
  return `${sign}${pad(m)}:${pad(sec)}`;
}

/**
 * Convert "12.50" dollar string → 1250 cents. Empty/invalid → null.
 */
export function dollarsToCents(input: string): number | null {
  if (input === '' || input == null) return null;
  const n = parseFloat(input);
  if (!isFinite(n)) return null;
  return Math.round(n * 100);
}

/**
 * Convert cents → dollar string for an input value. null → ''.
 */
export function centsToDollarsInput(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toString();
}

/**
 * Words that should stay upper-case when a field key becomes a label.
 * Without this, sms_consent reads as "Sms consent".
 */
const KEY_ACRONYMS = new Set([
  'sms', 'url', 'id', 'dob', 'zip', 'faq', 'ssn', 'api', 'ein', 'vat', 'cc', 'ip', 'utm',
]);

/**
 * Turn a form field's own key into a human label: your-email becomes
 * "Your email", first_name becomes "First name", smsConsent becomes
 * "SMS consent".
 *
 * formatStatus() is the nearest existing helper but only splits underscores and
 * sentence-cases the whole string, so your-email survives as "Your-email".
 */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_.\s]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);

  if (words.length === 0) return key;

  return words
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (KEY_ACRONYMS.has(lower)) return lower.toUpperCase();
      return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}
